import {inject, InjectionToken, Pipe, PipeTransform} from '@angular/core';
import {
    DANISH_DATE_SEPARATOR,
    dateFormatOptions,
    formatDate,
    TranslateService,
} from '@brunata-angular/util';
import {DateTime, DateTimeFormatOptions} from 'luxon';

/**
 * App-level default format for the `appdate` pipe. When no token is provided and the template
 * passes no explicit format, the pipe falls back to the locale numeric date format
 * (`dateFormatOptions`, e.g. "08.06.2026"). An app that wants a different default provides this
 * token at the root — ngwebbill provides `DateTime.DATE_MED` ("8. jun. 2026") to preserve the
 * format its templates relied on before migrating off the app-local pipe. Individual call sites
 * can opt back into the numeric format with the `'numeric'` shorthand (`{{ value | appdate: 'numeric' }}`).
 */
export const DATE_PIPE_DEFAULT_FORMAT = new InjectionToken<DateTimeFormatOptions>(
    'DATE_PIPE_DEFAULT_FORMAT',
);

@Pipe({
    name: 'appdate',
    standalone: true,
})
export class DatePipe implements PipeTransform {
    private readonly translateService = inject(TranslateService);
    private readonly defaultFormat = inject(DATE_PIPE_DEFAULT_FORMAT, {optional: true});
    private readonly danishDateSeparator = inject(DANISH_DATE_SEPARATOR);

    transform(
        value: DateTime | null | undefined,
        formatOptions?: DateTimeFormatOptions | 'numeric',
    ): string {
        if (value == null || !value.isValid) {
            return '';
        }
        // 'numeric' forces the locale numeric format, regardless of the app-default token.
        const resolved =
            formatOptions === 'numeric'
                ? dateFormatOptions
                : (formatOptions ?? this.defaultFormat ?? undefined);
        return formatDate(
            value,
            this.translateService.language,
            resolved,
            this.danishDateSeparator,
        );
    }
}
